/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {useDispatch, useSelector} from "react-redux"; // Import hooks for Redux state management
import {useTranslation} from "react-i18next"; // Import translation hook for internationalization
import {useEffect, useState} from "react"; // Import React hooks for side-effects and state management

// Importing API functions related to Employee Contractors and Cards
import {GetWorkTimeAccess, GetZoneTree} from "../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import {
    AnalizeCards,
    CreateMultipleCards,
    CreateSingleCard,
    GetAllCardFormats
} from "../../../reduxToolkit/Cards/CardsApi";

// Main component to handle the card creation process
const CreateCardApp = ({setCreationErrorsShow, onHide, setModified}) => {
    // Translation hook for localization
    const { t } = useTranslation();

    // Redux hooks to dispatch actions and get the state
    const dispatch = useDispatch();
    const { getAllCardFormats } = useSelector((state) => state.CardsSlice);

    // Component state hooks for various form inputs and validations
    const [facilityCode, setFacilityCode] = useState(''); // State for facility code
    const [number, setNumber] = useState(''); // State for card number
    const [format, setFormat] = useState(''); // State for card format
    const [addRange, setAddRange] = useState(false); // State to check if a range of numbers will be added
    const [emptyFields, setEmptyFields] = useState(false); // State for empty fields validation
    const [startingIn, setStartingIn] = useState(''); // State for starting number in a range
    const [endingIn, setEndingIn] = useState(''); // State for ending number in a range
    const [validStarting, setValidStarting] = useState(true); // State for validating starting number
    const [validEnding, setValidEnding] = useState(true); // State for validating ending number
    const [workshift, setWorkshift] = useState(''); // State for workshift selection
    const [page, setPage] = useState(0); // State for pagination (current page)
    const [rowsPerPage, setRowsPerPage] = useState(10); // State for rows per page in pagination
    const [orderby, setOrderby] = useState('id'); // State for sorting order of the data
    const [isValidFacilityCode, setIsValidFacilityCode] = useState(true); // State for sorting order of the data

    // Object to handle pagination and sorting parameters
    let contractPagination = {
        order: true, // Sorting order (ascending)
        page: page, // Current page number
        size: rowsPerPage, // Number of rows per page
        sortBy: orderby, // Sorting field
    };

    // useEffect hook to fetch work time access data whenever page or rowsPerPage changes
    useEffect(() => {
        if (workshift) {
            dispatch(GetWorkTimeAccess({ id: workshift, contractPagination }));
        } else {
            return null; // If no workshift is selected, skip fetching
        }
    }, [page, rowsPerPage]); // Dependencies: page and rowsPerPage

    // useEffect hook to reset the form and fetch required data on component mount
    useEffect(() => {
        resetForm(); // Reset the form fields
        dispatch(GetAllCardFormats()); // Fetch all card formats
        setEmptyFields(false); // Reset empty fields validation state
        setValidEnding(true); // Reset ending validation state
        setValidStarting(true); // Reset starting validation state
        dispatch(GetZoneTree()); // Fetch zone tree data
    }, []); // This effect runs once when the component mounts

    // Function to reset the form fields to their initial state
    const resetForm = () => {
        setFacilityCode('');
        setNumber('');
        setStartingIn('');
        setEndingIn('');
        setFormat('');
        setAddRange(false);
    };

    // Function to handle the confirmation of card creation
    const handleConfirm = () => {
        setEmptyFields(true); // Set empty fields flag to true before validation
        // Check if required fields are filled in and valid
        if (facilityCode !== '' &&
            ((!addRange && number !== '') || (addRange && startingIn !== '' && endingIn !== '' && validEnding && validStarting)) &&
            format !== '') {

            setEmptyFields(false); // Reset empty fields flag if validation passes

            if (addRange) { // If adding a range of card numbers
                const body = {
                    facilityCode: facilityCode,
                    cardFormatId: format,
                    expiretAt: 0,
                    numbers: arrayNumbers(startingIn, endingIn), // Generate the range of card numbers
                };

                // Dispatch action to analyze the cards
                dispatch(AnalizeCards(body)).then(
                    ({payload: {data: { data }}}) => {
                        // If no errors in the analyzed cards, proceed to create cards
                        if (data.length === 0) {
                            dispatch(CreateMultipleCards(body)).then(
                                ({payload: {data: { data, success }}}) => {
                                    if (success === true) {
                                        onHide();
                                        setModified(true);
                                    }
                                },
                            );
                        } else {
                            onHide();
                            setModified(true);
                            setCreationErrorsShow(true);
                        }
                    },
                );
            } else { // If creating a single card
                const body = {
                    facilityCode: facilityCode,
                    number: number,
                    cardFormatId: format,
                };
                // Dispatch action to create a single card
                dispatch(CreateSingleCard(body)).then(
                    ({payload: {data: { success }}}) => {
                        if (success === true) {
                            onHide();
                            setModified(true);
                        }
                    },
                );
            }
        }
    };

    // Function to generate an array of card numbers between the starting and ending values
    const arrayNumbers = (starting, ending) => {
        const result = [];

        if (starting > ending) { // If the starting number is greater than the ending, return an empty array
            return result;
        }

        for (let i = starting; i <= ending; i++) {
            result.push(i); // Push each number to the result array
        }

        return result; // Return the generated range of numbers
    };

    // Function to validate the starting number for the range
    const validateStarting = (newValue) => {
        if (endingIn === '') {
            setValidStarting(true); // If ending value is empty, no validation needed
        } else {
            if (newValue === '') {
                setEndingIn(''); // Reset ending value if starting value is empty
            } else if (newValue < parseInt(endingIn)) {
                setValidStarting(true); // Valid if starting number is less than ending number
            } else {
                setValidStarting(false); // Invalid if starting number is greater than or equal to ending number
            }
        }
    };

    // Function to validate the ending number for the range
    const validateEnding = (newValue) => {
        if (parseInt(startingIn) < newValue) {
            setValidEnding(true); // Valid if starting number is less than ending number
        } else {
            setValidEnding(false); // Invalid if starting number is greater than or equal to ending number
        }
    };

    return {
        t,
        emptyFields,
        facilityCode,
        setFacilityCode,
        addRange,
        setAddRange,
        number,
        setNumber,
        startingIn,
        setStartingIn,
        validStarting,
        endingIn,
        setEndingIn,
        validEnding,
        format,
        setFormat,
        getAllCardFormats,
        isValidFacilityCode,
        setIsValidFacilityCode,
        handleConfirm,
        validateStarting,
        validateEnding
    }
}

export default CreateCardApp;
